/** The scheme and host of a storefront URL, without a path or trailing slash. */
function normalizePreviewBaseUrl(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }

  try {
    const url =
      value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`
    const normalized = new URL(url)
    normalized.pathname = ''
    normalized.search = ''
    normalized.hash = ''
    return normalized.toString().replace(/\/$/, '')
  } catch {
    return null
  }
}

/** Storefront previews run on the shop's demo host (`<demo_id>.<VITE_DEMO_URL>`). */
export function resolveStorefrontPreviewBaseUrl(
  organization: { demo_id?: string | null } | null | undefined
): string | null {
  return buildDemoStorefrontUrl(organization?.demo_id, import.meta.env.VITE_DEMO_URL)
}

export function buildDemoStorefrontUrl(
  demoId: string | null | undefined,
  demoBaseUrl: string | null | undefined
): string | null {
  if (!demoId || typeof demoBaseUrl !== 'string' || demoBaseUrl.trim() === '') {
    return null
  }

  const baseUrl = demoBaseUrl.trim().replace(/\/+$/, '')

  try {
    if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
      const url = new URL(baseUrl)
      url.hostname = `${demoId}.${url.hostname}`
      url.pathname = ''
      url.search = ''
      url.hash = ''

      return normalizePreviewBaseUrl(url.toString())
    }

    return normalizePreviewBaseUrl(`${demoId}.${baseUrl}`)
  } catch {
    return null
  }
}

export function buildStorefrontPreviewUrlForPublicPath({
  baseUrl,
  previewToken,
  publicPath,
}: {
  baseUrl: string | null | undefined
  previewToken: string | null | undefined
  publicPath: string | null | undefined
}): string | null {
  const normalizedBaseUrl = normalizePreviewBaseUrl(baseUrl)

  if (!normalizedBaseUrl || !previewToken) {
    return null
  }

  const normalizedPath =
    typeof publicPath === 'string' && publicPath.trim() !== '' ? publicPath.trim() : '/'
  const previewPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`
  const routePath = `/_preview/${previewToken}${previewPath === '/' ? '' : previewPath}`
  return new URL(routePath, `${normalizedBaseUrl}/`).toString()
}
